import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { CATEGORIES } from "../../constants/siteData";

export const Categories = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section
      id="categories"
      className="relative bg-navy py-24 md:py-32 overflow-hidden"
      data-testid="categories-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <p className="kicker mb-5">Product Categories</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.03] tracking-tighter text-cream">
              A shelf-ready portfolio — <span className="text-coral">nine categories</span> deep.
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 text-white/60"
          >
            From beverages and pantry staples to haircare and appliances, our sourcing network
            covers every FMCG category your customers demand — in bulk formats and at wholesale
            prices.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {CATEGORIES.map((cat, i) => (
            <motion.div
              key={cat.slug}
              onClick={() => setSelected(cat)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`cat-card rounded-xl block cursor-pointer ${
                i === 0 || i === 4 ? "sm:col-span-2 sm:row-span-1 lg:col-span-1" : ""
              }`}
              data-testid={`category-card-${cat.slug}`}
            >
              <div className="aspect-[4/5] w-full relative">
                <img
                  src={cat.image}
                  alt={cat.name}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="cat-overlay absolute inset-0" />
                <div className="absolute inset-0 p-6 md:p-7 flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-white/70 bg-black/30 backdrop-blur-md px-3 py-1.5 rounded-full">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="w-10 h-10 rounded-full bg-coral text-navy flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <ArrowUpRight size={18} />
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-coral tracking-widest uppercase mb-2">
                      {cat.count}
                    </p>
                    <h3 className="font-display text-2xl md:text-3xl text-cream leading-tight">
                      {cat.name}
                    </h3>
                    <div className="mt-3 flex items-center gap-2 text-sm text-white/80">
                      <span className="w-6 h-[1px] bg-coral" />
                      Explore range
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Image Popup */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
            onClick={() => setSelected(null)}
            data-testid="category-popup-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-4xl w-full max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
              data-testid="category-popup-content"
            >
              <button
                onClick={() => setSelected(null)}
                className="absolute -top-3 -right-3 z-10 w-10 h-10 rounded-full bg-coral text-white flex items-center justify-center hover:bg-coral/80"
                style={{ transitionProperty: "background-color", transitionDuration: "200ms" }}
                data-testid="category-popup-close"
              >
                <X size={20} />
              </button>
              <img
                src={selected.image}
                alt={selected.name}
                className="w-full h-auto max-h-[85vh] object-contain rounded-xl"
              />
              <p className="text-center text-cream font-display text-xl mt-4">{selected.name}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
